import React from "react";
import {
  Box,
  IconButton,
  Menu as MuiMenu,
  MenuItem,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  Slide,
  Backdrop,
  List,
  ListItemButton,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import { ViewName } from "../../../types";
import { VIEWS } from "../../../constants/views";
import LanguageSelector from "../../LanguageSelector";
import { logo } from "../../../assets";

interface MenuProps {
  currentView: ViewName;
  role: 1 | 2 | null;
  userId: string | null;
  isRTL: boolean;
  getTranslation: (key: string) => string;
  anchorEl: HTMLElement | null;
  open: boolean;
  languageMenuOpen: boolean;
  handleMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  handleLanguageMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleLanguageMenuClose: () => void;
  handleLanguageChange: () => void;
  handleSignOut: () => void;
  setCurrentView: (view: ViewName) => void;
}

export const Menu: React.FC<MenuProps> = ({
  currentView,
  role,
  userId,
  isRTL,
  getTranslation,
  anchorEl,
  open,
  languageMenuOpen,
  handleMenuClick,
  handleMenuClose,
  handleLanguageMenuClick,
  handleLanguageMenuClose,
  handleLanguageChange,
  handleSignOut,
  setCurrentView,
}) => {
  return (
    <>
      <Tooltip
        title={getTranslation("Menu") || "Menu"}
        arrow
        sx={{
          "& .MuiTooltip-tooltip": {
            backgroundColor: "var(--color-primary)",
            color: "var(--color-text-inverse)",
            fontSize: "14px",
            fontWeight: 500,
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            maxWidth: "200px",
            textAlign: "center",
            fontFamily: "HerbalifeNatural-Regular, Arial, sans-serif",
          },
          "& .MuiTooltip-arrow": {
            color: "var(--color-primary)",
          },
        }}
      >
        <IconButton
          size="large"
          aria-label="menu"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          onClick={handleMenuClick}
          sx={{ color: "black" }}
        >
          <MenuIcon />
        </IconButton>
      </Tooltip>

      <MuiMenu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1.5,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
            padding: "0px !important",
            "& .MuiList-root": {
              paddingLeft: 0,
              paddingRight: 0,
            },
            "& .MuiMenuItem-root": {
              paddingLeft: 1,
              paddingRight: 1,
            },
          },
        }}
      >
        {/* Language - show always */}
        {((role === 1 || userId) ||
          ["signup", "contact"].includes(currentView)) && (
          <MenuItem onClick={handleLanguageMenuClick}>
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {getTranslation("Language") || "Language"}
            </ListItemText>
          </MenuItem>
        )}

        {/* Sign Out - show when logged in and not on signup/contact */}
        {(role === 1 || userId) &&
          !["signup", "contact"].includes(currentView) && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleSignOut}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {getTranslation("Txt893") || "Sign Out"}
                </ListItemText>
              </MenuItem>
            </>
          )}
      </MuiMenu>

      {/* Language Menu - Slide-in panel */}
      <Backdrop
        open={languageMenuOpen}
        onClick={handleLanguageMenuClose}
        sx={{
          zIndex: 1299,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />
      <Slide
        direction="left"
        in={languageMenuOpen}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: isRTL ? "0px" : "auto",
            right: isRTL ? "auto" : "0px",
            height: { xs: "100dvh", sm: "100vh", md: "100vh" },
            maxHeight: { xs: "100dvh", sm: "100vh", md: "100vh" },
            width: { xs: "100%", sm: 400, md: 450 },
            maxWidth: "90vw",
            backgroundColor: "white",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            zIndex: 1300,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="HL Skin Logo"
              sx={{
                height: { xs: "1.5rem", sm: "1.5rem", md: "2rem" },
                width: "auto",
                objectFit: "contain",
              }}
            />
            <IconButton
              onClick={handleLanguageMenuClose}
              sx={{ color: "black" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ 
            flex: 1, 
            display: "flex", 
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}>
            <List
              sx={{
                p: 0,
                paddingLeft: "0 !important",
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                "&.MuiList-root": {
                  paddingLeft: "0 !important",
                },
              }}
            >
              {/* Language - show for logged in users only */}
              {(role === 1 || userId) && (
                <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <LanguageSelector
                    variant="menu"
                    onLanguageChange={() => {
                      handleLanguageChange();
                    }}
                  />
                </Box>
              )}

              {/* Edit Profile - show when logged in and not on signup/contact/edit-profile */}
              {(role === 2 || userId) &&
                !["signup", "contact", "edit-profile"].includes(currentView) && (
                  <>
                    <Divider />
                    <ListItemButton
                      onClick={() => {
                        handleLanguageMenuClose();
                        // Store current view to restore on back button
                        sessionStorage.setItem("editProfilePrevView", currentView);
                        setCurrentView("edit-profile");
                      }}
                      sx={{
                        py: 2,
                        px: 3,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: "var(--color-text-primary)" }}>
                        <EditIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslation("Txt963")}
                        primaryTypographyProps={{
                          fontSize: "1rem",
                          fontWeight: 500,
                          color: "var(--color-text-primary)",
                        }}
                      />
                    </ListItemButton>
                  </>
                )}
              {/* Deactivated Users - show only for distributors (role === 1) and not on signup/contact */}
              {role === 1 && !["signup", "contact", "DistributorDeactivatedUser"].includes(currentView) && (
                <>
                  <Divider />
                  <ListItemButton 
                    sx={{
                      py: 2,
                      px: 3,
                    }}
                    onClick={() => {
                      handleLanguageMenuClose();
                      // Store current view before navigating
                      if (currentView) {
                        sessionStorage.setItem('deactivatedUsersPrevView', currentView);
                      }
                      setCurrentView(VIEWS.DISTRIBUTOR_DEACTIVATED_USER);
                    }}>
                    <ListItemIcon sx={{ minWidth: 40, color: "var(--color-text-primary)" }}>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={getTranslation("Txt981") || "Deactivated Users"}
                      primaryTypographyProps={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        color: "var(--color-text-primary)",
                      }}
                    />
                  </ListItemButton>
                </>
              )}

              {/* Sign Out - show when logged in and not on signup/contact */}
              {(role === 1 || userId) &&
                !["signup", "contact"].includes(currentView) && (
                  <>
                    <Divider />
                    <ListItemButton
                      onClick={() => {
                        handleLanguageMenuClose();
                        handleSignOut();
                      }}
                      sx={{
                        py: 2,
                        px: 3,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: "var(--color-text-primary)" }}>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslation("Txt893") || "Sign Out"}
                        primaryTypographyProps={{
                          fontSize: "1rem",
                          fontWeight: 500,
                          color: "var(--color-text-primary)",
                        }}
                      />
                    </ListItemButton>
                  </>
                )}
            </List>
          </Box>
        </Box>
      </Slide>
    </>
  );
};
